/**
 * SuiteFooter — global HustleSuite footer.
 *
 * 4-column layout (Modules / Legal / Company / Support) + bottom row
 * (copyright · language picker · social · optional partnership line).
 *
 * Design intent: brand-neutral across every HustleSuite module (Travel,
 * Find, Plaza, Stays, Shop, Recipes, Meet, Assistant). Not restaurant-
 * specific — that footer lives in `SiteLayout.jsx`. Prefer this one for
 * every module-host and Suite-native page.
 *
 * Trivago partnership line: shown on travel/accommodation pages only
 * (pass `trivagoContext="hotel"` or `"travel"`). A/B tested with two
 * copy variants — the assignment is sticky per session via localStorage
 * so the same user sees the same variant on repeat visits.
 *
 * Data-testids:
 *   suite-footer
 *   suite-footer-modules / -legal / -company / -support
 *   suite-footer-copyright
 *   suite-footer-language
 *   trivago-partnership-footer (only when trivagoContext is set)
 *   trivago-partnership-link
 *   trivago-partnership-variant-a / -variant-b (mutually exclusive)
 */
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const MODULES = [
  { href: "https://app.hustlefind.au",       label: "HustleFinder — directory" },
  { href: "https://app.hustleplaza.au",      label: "HustlePlaza — marketplace" },
  { href: "https://app.hustlestays.au",      label: "HustleStays — accommodation" },
  { href: "https://app.hustletravel.au",     label: "HustleTravel — road trips" },
  { href: "https://app.hustleshop.au",       label: "HustleShop — checkout" },
  { href: "https://app.hustleconnect.au",    label: "HustleConnect — WordPress" },
  { href: "/hustlesuite-assistant",          label: "HustleSuite Assistant — extension" },
];

const LEGAL = [
  { href: "/legal/terms",       label: "Terms of use" },
  { href: "/legal/privacy",     label: "Privacy policy" },
  { href: "/legal/refunds",     label: "Refunds" },
  { href: "/legal/gst",         label: "GST" },
  { href: "/legal/cookies",     label: "Cookie policy" },
  { href: "/legal/accessibility", label: "Accessibility" },
];

const COMPANY = [
  { href: "/about",       label: "About HustleSuite" },
  { href: "/contact",     label: "Contact us" },
  { href: "/careers",     label: "Careers" },
  { href: "/press",       label: "Press" },
  { href: "/blog",        label: "Blog" },
];

const SUPPORT = [
  { href: "/support",     label: "Help centre" },
  { href: "/status",      label: "Service status" },
  { href: "/report",      label: "Report an issue" },
  { href: "/community",   label: "Community" },
];

/* ── Trivago A/B ── */
const TRIVAGO_VARIANTS = {
  a: {
    prefix: "We proudly support hotel booking with",
    linkText: "Trivago",
  },
  b: {
    prefix: "Compare 200+ sites for the best hotel price on",
    linkText: "Trivago",
  },
};

/** Sticky per-browser bucketing. Assigned lazily on first render;
 *  persisted so a repeat visitor sees the same variant and click-
 *  through rates aren't inflated by the flicker cost of re-assignment. */
function useTrivagoVariant() {
  return useMemo(() => {
    try {
      let v = localStorage.getItem("hp_trivago_ab");
      if (v !== "a" && v !== "b") {
        v = Math.random() < 0.5 ? "a" : "b";
        localStorage.setItem("hp_trivago_ab", v);
      }
      return v;
    } catch { return "a"; }
  }, []);
}

const beaconEndpoint =
  (typeof window !== "undefined"
    && (window.REACT_APP_BACKEND_URL
        || process.env.REACT_APP_BACKEND_URL
        || window.location.origin))
    + "/api/analytics/conversion-event";

/** Fire-and-forget click log — never awaits, never blocks navigation. */
function logTrivagoClick(variant, context) {
  try {
    // Use sendBeacon so the click doesn't get killed by cross-origin
    // navigation. Fall back to axios if unavailable.
    const payload = JSON.stringify({
      event: `trivago_partnership_click_${variant}`,
      ref: context || "unknown",
    });
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(beaconEndpoint, blob);
    } else {
      axios.post(beaconEndpoint, JSON.parse(payload)).catch(() => {});
    }
  } catch { /* never block the user's click */ }
}

/** Fire once on first render — records that the variant was viewed
 *  (denominator for CTR). */
function logTrivagoImpression(variant, context) {
  try {
    const key = `hp_trivago_imp_${variant}_${context || "any"}`;
    // Only log once per session — impressions can double-count with
    // React strict mode + route re-renders.
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const payload = JSON.stringify({
      event: `trivago_partnership_impression_${variant}`,
      ref: context || "unknown",
    });
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(beaconEndpoint, blob);
    } else {
      axios.post(beaconEndpoint, JSON.parse(payload)).catch(() => {});
    }
  } catch { /* */ }
}

function TrivagoLine({ context }) {
  const variant = useTrivagoVariant();
  const cfg = TRIVAGO_VARIANTS[variant] || TRIVAGO_VARIANTS.a;
  useEffect(() => { logTrivagoImpression(variant, context); }, [variant, context]);
  return (
    <div
      data-testid="trivago-partnership-footer"
      className="border-t border-stone-800/60 mt-8 pt-4 text-center"
    >
      <p
        data-testid={`trivago-partnership-variant-${variant}`}
        className="text-[11px] italic text-stone-400"
      >
        {cfg.prefix}{" "}
        <a
          href="https://www.trivago.com.au/"
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-testid="trivago-partnership-link"
          onClick={() => logTrivagoClick(variant, context)}
          className="underline decoration-dotted underline-offset-2 hover:text-emerald-400"
        >
          {cfg.linkText}
        </a>
        .
      </p>
    </div>
  );
}

function Column({ title, links, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-400 mb-3">
        {title}
      </p>
      <ul className="space-y-1.5 text-sm text-stone-300">
        {links.map((l) => {
          const external = l.href.startsWith("http");
          return (
            <li key={l.href}>
              {external ? (
                <a href={l.href} target="_blank" rel="noopener noreferrer"
                   className="hover:text-white transition-colors">
                  {l.label}
                </a>
              ) : (
                <Link to={l.href} className="hover:text-white transition-colors">
                  {l.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Props:
 *   trivagoContext — one of `"hotel"`, `"travel"`, `"stays"`, `"finder"`
 *     etc. When set to any truthy value the partnership line renders.
 *     Leave `undefined` to omit the line entirely.
 *   className     — optional; applied to the outer <footer>.
 */
export default function SuiteFooter({ trivagoContext, className = "" }) {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="suite-footer"
      className={`bg-stone-950 text-stone-100 mt-16 ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <Column title="Modules" links={MODULES} testid="suite-footer-modules" />
          <Column title="Legal"   links={LEGAL}   testid="suite-footer-legal" />
          <Column title="Company" links={COMPANY} testid="suite-footer-company" />
          <Column title="Support" links={SUPPORT} testid="suite-footer-support" />
        </div>

        {/* Bottom row */}
        <div className="mt-12 pt-6 border-t border-stone-800/70 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-stone-500">
          <p data-testid="suite-footer-copyright">
            © {year} HustleSuite. Australian-made, family-run.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <span data-testid="suite-footer-language" className="text-stone-500">
              🌐 Language: <span className="text-stone-300">set in header</span>
            </span>
            <span aria-hidden className="text-stone-700">·</span>
            <a href="https://www.linkedin.com/company/hustlesuite" target="_blank" rel="noopener noreferrer"
               className="hover:text-white" data-testid="suite-footer-linkedin">LinkedIn</a>
            <a href="https://x.com/hustlesuite" target="_blank" rel="noopener noreferrer"
               className="hover:text-white" data-testid="suite-footer-x">X</a>
            <a href="https://www.instagram.com/hustlesuite" target="_blank" rel="noopener noreferrer"
               className="hover:text-white" data-testid="suite-footer-instagram">Instagram</a>
          </div>
        </div>

        {/* GST stance — Australian tax transparency line. Members always
            see inc-GST pricing; vendors always work in ex-GST numbers. */}
        <p data-testid="suite-footer-gst"
           className="mt-4 text-[11px] text-stone-500 leading-relaxed">
          <span className="text-stone-300 font-semibold">GST stance:</span>{" "}
          All member and consumer prices displayed across the HustleSuite
          are shown <span className="text-stone-300">including 10% Australian GST</span>{" "}
          — no surprises at checkout. All vendor-facing pricing, payouts,
          commission tables and rate cards are quoted <span className="text-stone-300">exclusive of GST</span>{" "}
          (ex-GST); the GST component is calculated and remitted separately
          via the Stripe Connect payout ledger.
        </p>

        {trivagoContext && <TrivagoLine context={trivagoContext} />}
      </div>
    </footer>
  );
}
